/**
 * GridCrash — Achievement Engine
 *
 * Parallel to the score-unlock system. Tracks gameplay milestones
 * (streaks, flags, wins across modes, lifetime score tiers) and
 * rewards players with equippable emoji badges.
 *
 * Storage: localStorage with in-memory fallback (same pattern as unlocks.ts)
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Achievement {
  id: string;
  emoji: string;
  name: string;
  description: string;
  /** For score-based: points threshold. For others: undefined. */
  scoreThreshold?: number;
  /** Equippable player title granted by this achievement (see titles.ts). */
  title?: string;
}

export interface AchievementState {
  /** IDs of earned achievements */
  earned: string[];
  /** Current win streak (resets on loss) */
  winStreak: number;
  /** Difficulty IDs won at least once */
  modesWon: string[];
  /** Total correct flags placed across all games */
  totalFlags: number;
}

export interface AchievementResult {
  /** Achievements newly earned this game */
  justEarned: Achievement[];
  /** Updated full state */
  state: AchievementState;
}

// ─── Achievement Table ────────────────────────────────────────────────────────

export const ACHIEVEMENTS: Achievement[] = [
  // ── Streak ──
  { id: "win_streak_3",  emoji: "🔥", name: "Hat Trick",     description: "Win 3 games in a row", title: "Hat Trick" },
  { id: "win_streak_10", emoji: "⚡", name: "Unstoppable",   description: "Win 10 games in a row", title: "Unstoppable" },

  // ── Mode mastery ──
  { id: "all_modes",     emoji: "🌐", name: "World Conqueror", description: "Win on Easy, Medium, and Expert", title: "World Conqueror" },

  // ── Flagging ──
  { id: "flag_25",  emoji: "🚩", name: "Flag Starter",  description: "Place 25 correct flags total" },
  { id: "flag_100", emoji: "🎯", name: "Sharpshooter",  description: "Place 100 correct flags total", title: "Sharpshooter" },
  { id: "flag_250", emoji: "💣", name: "Bomb Defuser",  description: "Place 250 correct flags total", title: "Bomb Defuser" },

  // ── Score milestones ──
  { id: "score_1k",   emoji: "🥉", name: "Bronze",        description: "Reach 1,000 lifetime points",   scoreThreshold: 1_000   },
  { id: "score_5k",   emoji: "🥈", name: "Silver",        description: "Reach 5,000 lifetime points",   scoreThreshold: 5_000   },
  { id: "score_10k",  emoji: "🥇", name: "Gold",          description: "Reach 10,000 lifetime points",  scoreThreshold: 10_000, title: "Gold" },
  { id: "score_15k",  emoji: "💠", name: "Sapphire",      description: "Reach 15,000 lifetime points",  scoreThreshold: 15_000  },
  { id: "score_20k",  emoji: "💜", name: "Amethyst",      description: "Reach 20,000 lifetime points",  scoreThreshold: 20_000  },
  { id: "score_25k",  emoji: "🩵", name: "Aqua",          description: "Reach 25,000 lifetime points",  scoreThreshold: 25_000  },
  { id: "score_30k",  emoji: "💚", name: "Emerald",       description: "Reach 30,000 lifetime points",  scoreThreshold: 30_000  },
  { id: "score_35k",  emoji: "🧡", name: "Amber",         description: "Reach 35,000 lifetime points",  scoreThreshold: 35_000  },
  { id: "score_40k",  emoji: "❤️", name: "Ruby",          description: "Reach 40,000 lifetime points",  scoreThreshold: 40_000  },
  { id: "score_45k",  emoji: "🤍", name: "Platinum",      description: "Reach 45,000 lifetime points",  scoreThreshold: 45_000  },
  { id: "score_50k",  emoji: "💎", name: "Diamond",       description: "Reach 50,000 lifetime points",  scoreThreshold: 50_000, title: "Diamond" },
  { id: "score_55k",  emoji: "🌙", name: "Moonstone",     description: "Reach 55,000 lifetime points",  scoreThreshold: 55_000  },
  { id: "score_60k",  emoji: "⭐", name: "Stardust",      description: "Reach 60,000 lifetime points",  scoreThreshold: 60_000  },
  { id: "score_65k",  emoji: "🌠", name: "Meteor",        description: "Reach 65,000 lifetime points",  scoreThreshold: 65_000  },
  { id: "score_70k",  emoji: "🪐", name: "Saturn",        description: "Reach 70,000 lifetime points",  scoreThreshold: 70_000  },
  { id: "score_75k",  emoji: "🌌", name: "Nebula",        description: "Reach 75,000 lifetime points",  scoreThreshold: 75_000, title: "Nebula" },
  { id: "score_80k",  emoji: "🔮", name: "Oracle",        description: "Reach 80,000 lifetime points",  scoreThreshold: 80_000  },
  { id: "score_85k",  emoji: "🧿", name: "Amulet",        description: "Reach 85,000 lifetime points",  scoreThreshold: 85_000  },
  { id: "score_90k",  emoji: "👁️", name: "All-Seeing",    description: "Reach 90,000 lifetime points",  scoreThreshold: 90_000  },
  { id: "score_95k",  emoji: "🦋", name: "Transcendent",  description: "Reach 95,000 lifetime points",  scoreThreshold: 95_000  },
  { id: "score_100k", emoji: "🫅", name: "Sovereign",     description: "Reach 100,000 lifetime points", scoreThreshold: 100_000, title: "Sovereign" },
];

// ─── Storage ──────────────────────────────────────────────────────────────────

const KEY = "gridcrash.achievements.state";

let memState: AchievementState | null = null;

const DEFAULT_STATE: AchievementState = {
  earned: [],
  winStreak: 0,
  modesWon: [],
  totalFlags: 0,
};

function readState(): AchievementState {
  if (memState) return memState;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) { memState = { ...DEFAULT_STATE }; return memState; }
    const parsed = JSON.parse(raw) as Partial<AchievementState>;
    memState = {
      earned:    Array.isArray(parsed.earned)    ? parsed.earned.filter((e): e is string => typeof e === "string") : [],
      winStreak: typeof parsed.winStreak === "number" ? parsed.winStreak : 0,
      modesWon:  Array.isArray(parsed.modesWon)  ? parsed.modesWon.filter((e): e is string => typeof e === "string") : [],
      totalFlags: typeof parsed.totalFlags === "number" ? parsed.totalFlags : 0,
    };
    return memState;
  } catch {
    memState = { ...DEFAULT_STATE };
    return memState;
  }
}

function writeState(state: AchievementState): void {
  memState = state;
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* private browsing */ }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Called at end of each game. Pass the game result and we update all
 * counters and return any newly earned achievements.
 */
export function recordGameResult(params: {
  won: boolean;
  difficultyId: string;
  correctFlags: number;
  lifetimeScore: number;
}): AchievementResult {
  const { won, difficultyId, correctFlags, lifetimeScore } = params;
  const state = readState();
  const prevEarned = new Set(state.earned);
  const justEarned: Achievement[] = [];

  // ── Streak ──
  if (won) {
    state.winStreak += 1;
  } else {
    state.winStreak = 0;
  }

  // ── Modes won ──
  if (won && !state.modesWon.includes(difficultyId)) {
    state.modesWon.push(difficultyId);
  }

  // ── Flags ──
  state.totalFlags += correctFlags;

  // ── Check all achievements ──
  for (const a of ACHIEVEMENTS) {
    if (prevEarned.has(a.id)) continue; // already earned

    let earned = false;

    if (a.id === "win_streak_3")  earned = state.winStreak >= 3;
    else if (a.id === "win_streak_10") earned = state.winStreak >= 10;
    // Engine difficulty ids are easy/medium/hard ("hard" renders as Expert).
    else if (a.id === "all_modes") earned = ["easy", "medium", "hard"].every(m => state.modesWon.includes(m));
    else if (a.id === "flag_25")  earned = state.totalFlags >= 25;
    else if (a.id === "flag_100") earned = state.totalFlags >= 100;
    else if (a.id === "flag_250") earned = state.totalFlags >= 250;
    else if (a.scoreThreshold !== undefined) earned = lifetimeScore >= a.scoreThreshold;

    if (earned) {
      state.earned.push(a.id);
      justEarned.push(a);
    }
  }

  writeState(state);
  return { justEarned, state };
}

/** Get all earned Achievement objects, in earn order. */
export function getEarnedAchievements(): Achievement[] {
  const { earned } = readState();
  return earned
    .map(id => ACHIEVEMENTS.find(a => a.id === id))
    .filter((a): a is Achievement => a !== undefined);
}

/** Full achievement state — used by the picker UI. */
export function getAchievementState(): AchievementState {
  return readState();
}
